import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { format, parseISO } from 'date-fns';
import { formatDateForExport, formatNumber } from 'utils/helpers/format';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ButtonGroup, MenuItem } from '@mui/material';
import MDropdownMenuButton from 'common/MDropdownMenuButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BulkActionFooter from 'common/BulkActionFooter';
import PersistentDataGrid from 'common/PersistentDataGrid';
import { useUserRolePermissions } from 'hooks';
import { AuthContext } from 'authentication/AuthContext';
import { isEditableStatus, shipmentStatusTooltips } from '../utils';
import StatusChip from '../components/StatusChip';

const ShipmentsGridView = ({
  canUseBulkActions,
  isProcessing,
  loading,
  onAcceptShipments,
  onCancel,
  onPaginationChange,
  onResetSelection,
  onSelectionModelChange,
  paginationModel,
  rowSelectionModel,
  shipments,
  totalResults,
}) => {
  const navigate = useNavigate();
  const { isProductShippingManage } = useUserRolePermissions();
  const { affiliates } = React.useContext(AuthContext);

  const isBatchOwnerAndManager = batchOwnerName =>
    affiliates.some(({ customerNameShort }) => R.equals(customerNameShort, batchOwnerName));

  const isShipmentAndBatchOwner = params =>
    R.and(
      affiliates.some(({ customerNameShort }) =>
        R.equals(customerNameShort, params.row.shipperName)
      ),
      !isBatchOwnerAndManager(params.row.batchOwnerName)
    );

  const handleEditClick = params => {
    navigate(`./${params.id}/edit`);
  };
  const handleViewClick = params => {
    navigate(`./${params.id}/edit`);
  };

  const canSelectShipment = useCallback(
    row =>
      canUseBulkActions &&
      row.status === 'Unconfirmed' &&
      affiliates.some(
        ({ customerNameShort, shipper }) => shipper && customerNameShort === row.shipperName
      ),
    [affiliates, canUseBulkActions]
  );

  const selectedShipmentIds = useMemo(
    () => new Set(rowSelectionModel.map(String)),
    [rowSelectionModel]
  );

  const isShipmentSelectable = useCallback(
    ({ row }) => canSelectShipment(row) || selectedShipmentIds.has(String(row.shipmentId)),
    [canSelectShipment, selectedShipmentIds]
  );

  const currentPageRowIds = useMemo(
    () => shipments.map(row => String(row.shipmentId)),
    [shipments]
  );
  const currentPageSelection = useMemo(
    () => rowSelectionModel.filter(id => currentPageRowIds.includes(String(id))),
    [currentPageRowIds, rowSelectionModel]
  );
  const selectedCount = rowSelectionModel.length;
  const hasSelections = selectedCount > 0;
  const shipmentCountLabel = useMemo(() => {
    const totalDisplay = formatNumber(totalResults, { defaultValue: 0 });

    if (hasSelections) {
      return `${formatNumber(selectedCount, {
        defaultValue: 0,
      })} of ${totalDisplay} Shipments`;
    }

    return `${totalDisplay} Shipments`;
  }, [hasSelections, selectedCount, totalResults]);

  const CustomActionFooter = useCallback(
    () => (
      <Box
        sx={{
          '& > .MuiBox-root': {
            pr: 0,
          },
          width: 1,
        }}
      >
        <BulkActionFooter
          actions={[
            <MenuItem key="accept-shipments" onClick={onAcceptShipments}>
              Accept Shipments
            </MenuItem>,
          ]}
          disabled={!hasSelections || isProcessing}
          primaryActionButtonTitle="Bulk Actions"
        />
      </Box>
    ),
    [hasSelections, isProcessing, onAcceptShipments]
  );

  const columns = [
    {
      cellClassName: 'css-heap-redact',
      field: 'parentBatchId',
      flex: 0.8,
      headerName: 'Batch Id',
      renderCell: ({ row }) =>
        row.parentBatchId && isBatchOwnerAndManager(row.batchOwnerName) ? (
          <Typography
            color="primary"
            onClick={() => navigate(`../batches/batch/${R.prop('parentBatchId', row)}/edit`)}
            sx={{
              color: 'green',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            variant="text"
          >
            {R.prop('parentBatchId', row)}
          </Typography>
        ) : (
          <Typography variant="text">{R.prop('parentBatchId', row)}</Typography>
        ),
    },

    {
      cellClassName: 'css-heap-redact',
      field: 'shipmentId',
      flex: 0.8,
      headerName: 'Shipment Id',
      renderCell: ({ row }) =>
        isBatchOwnerAndManager(row.batchOwnerName) ||
        affiliates.some(({ customerNameShort }) => R.equals(customerNameShort, row.shipperName)) ? (
          <Typography
            color="primary"
            onClick={() => navigate(`../batches/shipment/${R.prop('shipmentId', row)}/edit`)}
            sx={{
              color: 'green',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            variant="text"
          >
            {R.prop('shipmentId', row)}
          </Typography>
        ) : (
          <Typography variant="text">{R.prop('shipmentId', row)}</Typography>
        ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'shipperName',
      flex: 1,
      headerName: 'Shipper',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'status',
      flex: 1,
      headerName: 'Status',
      renderCell: ({ row }) =>
        R.prop('status', row) && (
          <Tooltip placement="top-start" title={shipmentStatusTooltips[R.prop('status', row)]}>
            <StatusChip status={R.prop('status', row)} />
          </Tooltip>
        ),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'acceptance',
      flex: 0.8,
      headerName: 'Acceptance',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'originName',
      flex: 1,
      headerName: 'Origin',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'productCode',
      flex: 0.7,
      headerName: 'Product Code',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'productName',
      flex: 1,
      headerName: 'Product',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'volume',
      flex: 0.7,
      headerName: 'Volume(bbls)',
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'bookedVolume',
      flex: 0.7,
      headerName: 'Booked Volume',
      type: 'number',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'agreementCode',
      flex: 1,
      headerName: 'Agreement Code',
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'requestedDate',
      flex: 1,
      headerName: 'Requested Date',
      renderCell: ({ row }) =>
        R.prop('requestedDate', row)
          ? format(parseISO(R.prop('requestedDate', row)), 'MM/dd/yyyy')
          : null,
      valueFormatter: formatDateForExport(),
    },
    {
      cellClassName: 'css-heap-redact',
      field: 'action',
      flex: 1,
      headerName: 'Action',
      renderCell: params =>
        // eslint-disable-next-line no-restricted-globals
        R.prop('shipmentId', params.row) && (
          <>
            <Box>
              {isProductShippingManage &&
              isShipmentAndBatchOwner(params) &&
              isEditableStatus(params.row.status) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleEditClick(params)}>Edit</Button>
                </ButtonGroup>
              ) : isProductShippingManage && isEditableStatus(params.row.status) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleEditClick(params)}>Edit</Button>
                  <MDropdownMenuButton>
                    <MenuItem
                      onClick={() => navigate(`../batches/batch/${params.row.parentBatchId}/edit`)}
                    >
                      View Parent Batch
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        onCancel('shipment', params.row.shipmentId, params.row.isOnlyShipment)
                      }
                    >
                      Cancel Shipment
                    </MenuItem>
                  </MDropdownMenuButton>
                </ButtonGroup>
              ) : null}

              {isShipmentAndBatchOwner(params) && !isEditableStatus(params.row.status) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleViewClick(params)}>View</Button>
                </ButtonGroup>
              ) : isProductShippingManage && !isEditableStatus(params.row.status) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleViewClick(params)}>View</Button>
                  <MDropdownMenuButton>
                    <MenuItem
                      onClick={() => navigate(`../batches/batch/${params.row.parentBatchId}/edit`)}
                    >
                      View Parent Batch
                    </MenuItem>
                  </MDropdownMenuButton>
                </ButtonGroup>
              ) : !isProductShippingManage && isShipmentAndBatchOwner(params) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleViewClick(params)}>View</Button>
                </ButtonGroup>
              ) : !isProductShippingManage && !isShipmentAndBatchOwner(params) ? (
                <ButtonGroup orientation="horizontal" sx={{ width: '150px' }} variant="outlined">
                  <Button onClick={() => handleViewClick(params)}>View</Button>
                  <MDropdownMenuButton>
                    <MenuItem
                      onClick={() => navigate(`../batches/batch/${params.row.parentBatchId}/edit`)}
                    >
                      View Parent Batch
                    </MenuItem>
                  </MDropdownMenuButton>
                </ButtonGroup>
              ) : null}
            </Box>
          </>
        ),
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-disabled': {
          color: 'text.disabled',
        },
        '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root': {
          color: 'text.disabled',
        },
        '& .MuiDataGrid-scrollbar--horizontal': {
          display: 'none',
        },
        '& .MuiDataGrid-virtualScroller': {
          overflowX: 'hidden',
        },
        display: 'flex',
        height: { lg: 'calc(100vh - 257px)', xs: 'calc(100vh - 309px)' },
        minHeight: 400,
        overflow: 'hidden',
      }}
    >
      <PersistentDataGrid
        bulkActionFooter={canUseBulkActions ? CustomActionFooter : null}
        checkboxSelection={canUseBulkActions}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={row => String(row.shipmentId)}
        id="shipments"
        initialState={{
          aggregation: {
            model: {
              volume: 'sum',
            },
          },
          columns: {
            columnVisibilityModel: {
              productName: false,
            },
          },
          pinnedColumns: {
            left: [],
            right: ['action'],
          },
        }}
        isProcessing={isProcessing}
        isRowSelectable={isShipmentSelectable}
        keepNonExistentRowsSelected
        loading={loading || isProcessing}
        onPaginationModelChange={onPaginationChange}
        onReset={onResetSelection}
        onRowSelectionModelChange={canUseBulkActions ? onSelectionModelChange : undefined}
        pageSizeOptions={[50, 100, 150, 200]}
        paginationMode="server"
        paginationModel={paginationModel}
        paginationStatus={shipmentCountLabel}
        quickFilter
        rowCount={totalResults}
        rows={shipments}
        rowSelectionModel={canUseBulkActions ? currentPageSelection : undefined}
        showFilterButton
      />
    </Box>
  );
};
ShipmentsGridView.propTypes = {
  canUseBulkActions: bool,
  isProcessing: bool,
  loading: bool,
  onAcceptShipments: func,
  onCancel: func.isRequired,
  onPaginationChange: func,
  onResetSelection: func,
  onSelectionModelChange: func,
  paginationModel: shape({ page: number, pageSize: number }),
  rowSelectionModel: arrayOf(oneOfType([number, string])),
  shipments: arrayOf(shape({})).isRequired,
  totalResults: number,
};
ShipmentsGridView.defaultProps = {
  canUseBulkActions: false,
  isProcessing: false,
  loading: false,
  onAcceptShipments: null,
  onPaginationChange: null,
  onResetSelection: null,
  onSelectionModelChange: null,
  paginationModel: { page: 0, pageSize: 100 },
  rowSelectionModel: [],
  totalResults: 0,
};
export default ShipmentsGridView;
